import clsx from 'clsx';
import React from 'react';

import { Icon, type SVGIconComponent } from '@gitbook/spine-icons';
import { assert } from '@gitbook/util-ts';

import { Button, type ButtonKind, type ButtonProps } from '../Button';
import { type IconDefinition, renderIcon } from '../util';

interface BaseAlertProps {
    /** Semantic kind; drives icon, background, border, and default action color. Defaults to `default`. */
    kind?: AlertKind;
    /** If true, renders title and details on the same line (`title: details`). Forced on for `xsmall`. */
    inline?: boolean;
    /** Secondary explanatory text shown below (or after, when `inline`) the title. */
    details?: React.ReactNode;
    /** Override the kind's default icon. Pass any {@link IconDefinition}. */
    icon?: IconDefinition;
    /** Optional trailing call-to-action. Pass an {@link AlertAction} config object or any React node. */
    action?: AlertAction | React.ReactNode;
    className?: string;
    style?: React.CSSProperties;
    testID?: string;
    /** Shows a close button that calls this handler. Not allowed when `kind === 'danger'`. */
    onClose?: () => void;
}

type AlertSize = 'xsmall' | 'small' | 'medium';

interface XSmallAlertProps extends BaseAlertProps {
    /** `xsmall` forces `inline` and disallows `title`. */
    size: Extract<AlertSize, 'xsmall'>;
    title?: never;
}

interface RegularAlertProps extends BaseAlertProps {
    /** Visual density. Defaults to `medium`. */
    size?: Extract<AlertSize, 'small' | 'medium'>;
    /** Bold heading shown above (or before, when `inline`) the details. */
    title?: string;
}

export type AlertProps = XSmallAlertProps | RegularAlertProps;

export type AlertKind = 'default' | 'info' | 'danger' | 'success' | 'merge' | 'upgrade' | 'warning';
export type AlertAction = {
    kind?: ButtonKind;
    label: string;
    href?: ButtonProps['href'];
    target?: string;
    onPress?: ButtonProps['onPress'];
    leadingIcon?: IconDefinition;
    trailingIcon?: IconDefinition;
};

/**
 * Block-level feedback surface for conveying state or required action. Pairs a semantic-colored
 * container + icon with a `title`, optional `details`, an optional `action` button, and an
 * optional close button.
 *
 * Differs from {@link Status}, which is a compact inline pill for at-a-glance state.
 * Differs from {@link Badge}, which is a purely decorative/numerical indicator with no body copy.
 * Differs from `toast`, which is a transient, stacked notification rather than an embedded surface.
 */
export function Alert(props: AlertProps): React.ReactElement | null {
    const {
        kind = 'default',
        size = 'medium',
        details,
        action,
        className,
        style,
        testID,
        onClose,
    } = props;
    const sizeProps = sizePropsMap[size];
    const kindProps = alertKindPropsMap[kind];
    const title = size !== 'xsmall' ? props.title : undefined;
    const inline = size === 'xsmall' ? true : (props.inline ?? false);
    const IconComponent = props.icon ?? kindProps.icon;
    const centered = ['xsmall', 'medium'].includes(size) && (!title || !details);
    const hasRenderableAction =
        action !== null && action !== undefined && typeof action !== 'boolean';
    assert(!props.onClose || kind !== 'danger', '"onClose" is not allowed for "danger" alerts');

    return (
        <AlertContainer
            kind={kind}
            className={className}
            style={style}
            testID={testID}
            centered={centered}
            size={size}
            sizeProps={sizeProps}
        >
            <AlertContent
                size={size}
                inline={inline}
                centered={centered}
                icon={IconComponent}
                kindProps={kindProps}
                sizeProps={sizeProps}
            >
                {title ? (
                    <AlertTitle title={title} inline={inline} titleClass={sizeProps.titleClass} />
                ) : null}
                {details ? <AlertDetails details={details} inline={inline} /> : null}
            </AlertContent>
            {hasRenderableAction ? (
                isAlertAction(action) ? (
                    <AlertAction action={action} actionKind={kindProps.actionKind} size={size} />
                ) : (
                    <div className="flex shrink-0 self-center">{action}</div>
                )
            ) : null}
            {onClose && <AlertCloseButton onClose={onClose} centered={centered} />}
        </AlertContainer>
    );
}

// Container primitive
export type AlertContainerProps = {
    kind?: AlertKind;
    children: React.ReactNode;
    className?: string;
    style?: React.CSSProperties;
    testID?: string;
    centered: boolean;
    size: keyof typeof sizePropsMap;
    sizeProps: (typeof sizePropsMap)[keyof typeof sizePropsMap];
};

export function AlertContainer(props: AlertContainerProps): React.ReactElement {
    const {
        kind = 'default',
        children,
        className,
        style,
        testID,
        centered,
        size,
        sizeProps,
    } = props;
    const kindProps = alertKindPropsMap[kind];

    return (
        <div
            className={clsx(
                '@container relative flex w-full justify-between gap-2 rounded-lg border',
                centered && size !== 'xsmall' ? 'p-4' : sizeProps.containerClass,
                kindProps.border,
                kindProps.bg,
                className
            )}
            style={style}
            data-testid={testID}
        >
            {children}
        </div>
    );
}

// Content primitive, containing the icon and text primitives
export type AlertContentProps = {
    size: keyof typeof sizePropsMap;
    inline: boolean;
    centered: boolean;
    icon?: IconDefinition;
    children: React.ReactNode;
    kindProps: (typeof alertKindPropsMap)[keyof typeof alertKindPropsMap];
    sizeProps: (typeof sizePropsMap)[keyof typeof sizePropsMap];
};

export function AlertContent(props: AlertContentProps): React.ReactElement {
    const { size, inline, centered, icon, kindProps, sizeProps, children } = props;

    return (
        <div className={clsx('flex w-full', size === 'xsmall' ? 'gap-2' : 'gap-4')}>
            {icon &&
                renderIcon(icon, {
                    size: sizeProps.iconSize,
                    className: clsx(
                        'flex shrink-0',
                        centered ? 'self-center' : clsx('self-start', sizeProps.iconClass),
                        kindProps.iconColor
                    ),
                })}
            <div className={clsx('flex w-full', sizeProps.layoutClass)}>
                <div
                    className={clsx(
                        sizeProps.textLayoutClass,
                        !inline && clsx('flex flex-col', sizeProps.textGap)
                    )}
                >
                    {children}
                </div>
            </div>
        </div>
    );
}

// Title primitive
export type AlertTitleProps = {
    title: string;
    inline?: boolean;
    titleClass?: string;
};

export function AlertTitle(props: AlertTitleProps): React.ReactElement {
    const { title, inline, titleClass } = props;

    return (
        <div className={clsx('text-base', titleClass, inline && 'inline')}>
            {inline ? `${title}: ` : title}
        </div>
    );
}

// Details primitive
export type AlertDetailsProps = {
    details: React.ReactNode;
    inline?: boolean;
};

export function AlertDetails(props: AlertDetailsProps): React.ReactElement {
    const { details, inline } = props;
    return <div className={clsx('text-muted', inline && 'inline')}>{details}</div>;
}

// Optional action/button to show on the right side of the alert
export type AlertActionProps = {
    action: AlertAction;
    actionKind: ButtonKind;
    size: keyof typeof sizePropsMap;
};

export function AlertAction(props: AlertActionProps): React.ReactElement {
    const { action, actionKind, size } = props;

    return (
        <Button
            kind={action.kind ?? actionKind}
            label={action.label}
            href={action.href}
            size={size === 'xsmall' ? 'small' : 'medium'}
            onPress={action.onPress}
            className="flex shrink-0 self-center"
            target={action.target}
            trailingIcon={action.trailingIcon}
            leadingIcon={action.leadingIcon}
        />
    );
}

function isAlertAction(action: AlertProps['action']): action is AlertAction {
    return (
        typeof action === 'object' &&
        action !== null &&
        !React.isValidElement(action) &&
        'label' in action &&
        typeof action.label === 'string'
    );
}

// Close button primitive
export type AlertCloseButtonProps = {
    onClose: () => void;
    centered: boolean;
    className?: string;
};

export function AlertCloseButton(props: AlertCloseButtonProps): React.ReactElement {
    const { onClose, centered, className } = props;

    return (
        <Button
            iconOnly
            kind="close"
            label="Close"
            leadingIcon={Icon.X}
            onPress={() => {
                onClose?.();
            }}
            className={clsx('shrink-0', centered ? 'self-center' : 'self-start', className)}
        />
    );
}

/**
 * A map of Alert sizes props.
 */
export const sizePropsMap: Record<
    AlertSize,
    {
        iconSize: number;
        containerClass: string;
        iconClass: string;
        titleClass: string;
        layoutClass: string;
        textLayoutClass?: string;
        textGap: string;
    }
> = {
    xsmall: {
        iconSize: 16,
        containerClass: 'p-2',
        iconClass: 'mt-0.5',
        titleClass: 'text-ui-standout',
        layoutClass: 'flex-row items-center gap-2',
        textLayoutClass: 'flex-1',
        textGap: 'gap-1',
    },
    small: {
        iconSize: 16,
        containerClass: 'p-4',
        iconClass: 'mt-0.5',
        titleClass: 'text-ui-standout',
        layoutClass: 'flex-col items-start gap-3 @lg:flex-row @lg:items-center @lg:justify-between',
        textGap: 'gap-1',
    },
    medium: {
        iconSize: 16,
        containerClass: 'py-6 px-4',
        iconClass: 'mt-0.5',
        titleClass: 'text-ui-heading-small',
        layoutClass: 'justify-between items-center gap-4',
        textGap: 'gap-2',
    },
};

/**
 * A map of Alert kinds props.
 */
export const alertKindPropsMap: Record<
    AlertKind,
    {
        actionKind: ButtonKind;
        icon: SVGIconComponent;
        iconColor: string;
        bg: string;
        border: string;
    }
> = {
    default: {
        actionKind: 'primary',
        icon: Icon.InfoCircle,
        iconColor: 'text-base',
        bg: 'bg-base',
        border: 'border-base',
    },
    info: {
        actionKind: 'primary',
        icon: Icon.InfoCircle,
        iconColor: 'text-info-low dark:text-info-high',
        bg: 'bg-info-surface-subtle',
        border: 'border-info-interactive',
    },
    success: {
        actionKind: 'success',
        icon: Icon.CheckCircle,
        iconColor: 'text-success-low dark:text-success-high',
        bg: 'bg-success-surface-subtle',
        border: 'border-success-interactive',
    },
    danger: {
        actionKind: 'danger',
        icon: Icon.Alert,
        iconColor: 'text-danger-low dark:text-danger-high',
        bg: 'bg-danger-surface-subtle',
        border: 'border-danger-interactive',
    },
    merge: {
        actionKind: 'merge',
        icon: Icon.ChangeRequest,
        iconColor: 'text-merge-low dark:text-merge-high',
        bg: 'bg-merge-surface-subtle',
        border: 'border-merge-interactive',
    },
    upgrade: {
        actionKind: 'upgrade',
        icon: Icon.Upgrade,
        iconColor: 'text-upgrade-low dark:text-upgrade-high',
        bg: 'bg-upgrade-surface-subtle',
        border: 'border-upgrade-interactive',
    },
    warning: {
        actionKind: 'warning',
        icon: Icon.Warning,
        iconColor: 'text-warning-low dark:text-warning-high',
        bg: 'bg-warning-surface-subtle',
        border: 'border-warning-interactive',
    },
};
